import { useQuery } from '@tanstack/react-query';
import { useTickets } from './useMaintenance';
import { useBills } from './useAccounting';
import type { MaintenanceTicket } from '@repo/shared-types';
import type { Bill } from '@repo/shared-types/accounting/transactions';

/**
 * Centralized Vendor Metrics Hook
 * 
 * This hook provides a single source of truth for all vendor-derived metrics.
 * All calculations are performed consistently across PM and Vendor views.
 * 
 * Metrics are computed on-demand from tickets and bills, not stored in the vendor model.
 *
 * Vendor portal earnings / job aggregates should read from this hook — avoid duplicating ticket/bill math in views.
 */

export interface VendorMetrics {
  // Job Metrics
  completedJobs: number;
  activeJobs: number;
  totalJobs: number;
  
  // Financial Metrics
  totalEarnings: number;
  paidEarnings: number;
  pendingEarnings: number;
  averageJobValue: number;
  
  // Performance Metrics
  rating: number | null;
  responseTimeHours: number | null;
  completionRate: number;
  qualityScore: number | null;
  onTimeRate: number | null;
  
  // Detailed Data
  tickets: MaintenanceTicket[];
  bills: Bill[];
  paidBills: Bill[];
  pendingBills: Bill[];
}

/**
 * Calculate average rating from completed tickets with ratings
 */
const calculateAverageRating = (tickets: MaintenanceTicket[]): number | null => {
  const ratedTickets = tickets.filter(t => 
    (t.status === 'Completed' || t.status === 'Verified') && 
    t.rating && 
    t.rating > 0
  );
  
  if (ratedTickets.length === 0) return null;
  
  const totalRating = ratedTickets.reduce((sum, t) => sum + (t.rating || 0), 0);
  return Math.round((totalRating / ratedTickets.length) * 10) / 10; // Round to 1 decimal
};

/**
 * Calculate average response time in hours
 * Time between ticket assignment and first vendor action/status change
 */
const calculateAvgResponseTime = (tickets: MaintenanceTicket[]): number | null => {
  const assignedTickets = tickets.filter(t => 
    t.assignedVendorId && 
    (t.status !== 'Open')
  );
  
  if (assignedTickets.length === 0) return null;
  
  const responseTimes = assignedTickets.map(t => {
    const assignedDate = new Date(t.createdAt);
    const responseDate = new Date(t.completedAt || t.createdAt);
    const diffMs = responseDate.getTime() - assignedDate.getTime();
    return Math.max(0, diffMs / (1000 * 60 * 60)); // Convert to hours
  });
  
  const avgMs = responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length;
  return Math.round(avgMs * 10) / 10; // Round to 1 decimal
};

/**
 * Calculate completion rate as percentage of assigned jobs that were completed
 */
const calculateCompletionRate = (tickets: MaintenanceTicket[]): number => {
  const assignedTickets = tickets.filter(t => 
    t.assignedVendorId && 
    t.status !== 'Open'
  );
  
  if (assignedTickets.length === 0) return 100; // Default 100% if no jobs yet
  
  const completedTickets = assignedTickets.filter(t => 
    t.status === 'Completed' || 
    t.status === 'Verified' || 
    t.status === 'Approved'
  );
  
  return Math.round((completedTickets.length / assignedTickets.length) * 100);
};

/**
 * Calculate quality score based on multiple factors
 * - Rating (40%)
 * - Completion rate (30%)
 * - Response time (20%)
 * - Rework rate (10%)
 */
const calculateQualityScore = (
  tickets: MaintenanceTicket[],
  rating: number | null,
  completionRate: number,
  responseTime: number | null,
): number | null => {
  if (tickets.length === 0) return null;

  // Rating component (0-100 scale, rating is 0-5)
  const ratingScore = rating === null ? null : (rating / 5) * 100;
  
  // Completion rate is already 0-100
  const completionScore = completionRate;
  
  // Response time score (lower is better, normalize to 0-100)
  // Excellent: <2hrs = 100, Good: <4hrs = 80, Fair: <8hrs = 60, Poor: >8hrs = 40
  let responseScore: number | null = null;
  if (responseTime !== null) {
    responseScore = 100;
    if (responseTime > 8) responseScore = 40;
    else if (responseTime > 4) responseScore = 60;
    else if (responseTime > 2) responseScore = 80;
  }
  
  // Rework rate (jobs marked as needing rework)
  const reworkTickets = tickets.filter(t => t.status === 'Rejected' || t.approvalStatus === 'Rejected');
  const reworkRate = tickets.length > 0 ? (reworkTickets.length / tickets.length) : 0;
  const reworkScore = Math.max(0, 100 - (reworkRate * 100));
  
  // Weighted average
  const weightedParts: Array<{ value: number; weight: number }> = [
    { value: completionScore, weight: 0.3 },
    { value: reworkScore, weight: 0.1 },
  ];
  if (ratingScore !== null) weightedParts.push({ value: ratingScore, weight: 0.4 });
  if (responseScore !== null) weightedParts.push({ value: responseScore, weight: 0.2 });

  const totalWeight = weightedParts.reduce((sum, part) => sum + part.weight, 0);
  const weightedScore = weightedParts.reduce((sum, part) => sum + (part.value * part.weight), 0);
  return totalWeight > 0 ? Math.round(weightedScore / totalWeight) : null;
};

/**
 * Calculate on-time completion rate
 * Percentage of jobs completed by or before the due date
 */
const calculateOnTimeRate = (tickets: MaintenanceTicket[]): number | null => {
  const completedTicketsWithDueDate = tickets.filter((ticket) => {
    const dueDate = (ticket as MaintenanceTicket & { dueDate?: string }).dueDate;
    return (ticket.status === 'Completed' || ticket.status === 'Verified') && ticket.completedAt && dueDate;
  });
  
  if (completedTicketsWithDueDate.length === 0) return null;

  const onTimeCount = completedTicketsWithDueDate.filter((ticket) => {
    const dueDateValue = (ticket as MaintenanceTicket & { dueDate?: string }).dueDate;
    if (!dueDateValue) return false;
    const completedAt = new Date(ticket.completedAt as string).getTime();
    const dueDate = new Date(dueDateValue).getTime();
    return !Number.isNaN(completedAt) && !Number.isNaN(dueDate) && completedAt <= dueDate;
  }).length;

  return Math.round((onTimeCount / completedTicketsWithDueDate.length) * 100);
};

/**
 * Calculate metrics for a single vendor
 */
const calculateVendorMetrics = (vendorId: string, allTickets: MaintenanceTicket[], allBills: Bill[]): VendorMetrics => {
  // Filter vendor's tickets and bills
  const vendorTickets = allTickets.filter(t => t.assignedVendorId === vendorId);
  const vendorBills = allBills.filter(b => b.vendorId === vendorId);
  
  // Job counts
  const completedTickets = vendorTickets.filter(t => 
    t.status === 'Completed' || 
    t.status === 'Verified' || 
    t.status === 'Approved'
  );
  const activeTickets = vendorTickets.filter(t => 
    t.status !== 'Completed' && 
    t.status !== 'Verified' && 
    t.status !== 'Approved' &&
    t.status !== 'Rejected'
  );
  
  // Bill categorization
  const paidBills = vendorBills.filter(b => b.status === 'Paid');
  const pendingBills = vendorBills.filter(b => 
    b.status === 'Pending Approval' || 
    b.status === 'Approved'
  );
  
  // Financial calculations
  const totalEarnings = vendorBills.reduce((sum, b) => sum + (b.amountCents ?? 0) / 100, 0);
  const paidEarnings = paidBills.reduce((sum, b) => sum + (b.amountCents ?? 0) / 100, 0);
  const pendingEarnings = pendingBills.reduce((sum, b) => sum + (b.amountCents ?? 0) / 100, 0);
  const averageJobValue = paidBills.length > 0 
    ? paidEarnings / paidBills.length 
    : 0;
  
  // Performance metrics
  const rating = calculateAverageRating(vendorTickets);
  const responseTimeHours = calculateAvgResponseTime(vendorTickets);
  const completionRate = calculateCompletionRate(vendorTickets);
  const qualityScore = calculateQualityScore(vendorTickets, rating, completionRate, responseTimeHours);
  const onTimeRate = calculateOnTimeRate(vendorTickets);
  
  return {completedJobs: completedTickets.length, activeJobs: activeTickets.length, totalJobs: vendorTickets.length, tickets: vendorTickets, bills: vendorBills, totalEarnings, paidEarnings, pendingEarnings, averageJobValue, rating, responseTimeHours, completionRate, qualityScore, onTimeRate, paidBills, pendingBills};
};

/**
 * Main hook for vendor metrics
 */
export const useVendorMetrics = (vendorId: string | undefined) => {
  const { data: allTickets = [] } = useTickets();
  const { data: allBills = [] } = useBills();
  
  return useQuery({
    queryKey: ['vendors', 'metrics', vendorId],
    queryFn: (): VendorMetrics => {
      if (!vendorId) {
        return {
          completedJobs: 0,
          activeJobs: 0,
          totalJobs: 0,
          totalEarnings: 0,
          paidEarnings: 0,
          pendingEarnings: 0,
          averageJobValue: 0,
          rating: null,
          responseTimeHours: null,
          completionRate: 0,
          qualityScore: null,
          onTimeRate: null,
          tickets: [],
          bills: [],
          paidBills: [],
          pendingBills: [],
        };
      }
      
      return calculateVendorMetrics(vendorId, allTickets, allBills);
    },
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
  });
};

/**
 * Batch hook for all vendor metrics
 * Returns a map of vendorId -> VendorMetrics
 * More efficient than calling useVendorMetrics for each vendor individually
 */
export const useAllVendorMetrics = (vendorIds?: string[]) => {
  const { data: allTickets = [] } = useTickets();
  const { data: allBills = [] } = useBills();
  
  return useQuery({
    queryKey: ['vendors', 'metrics', 'all', vendorIds],
    queryFn: (): Record<string, VendorMetrics> => {
      // If vendorIds provided, only calculate for those vendors
      // Otherwise, calculate for all vendors found in tickets/bills
      const vendorIdSet = new Set<string>();
      
      if (vendorIds && vendorIds.length > 0) {
        vendorIds.forEach(id => vendorIdSet.add(id));
      } else {
        // Extract all vendor IDs from tickets and bills
        allTickets.forEach(t => {
          if (t.assignedVendorId) vendorIdSet.add(t.assignedVendorId);
        });
        allBills.forEach(b => {
          if (b.vendorId) vendorIdSet.add(b.vendorId);
        });
      }
      
      const metricsMap: Record<string, VendorMetrics> = {};
      
      vendorIdSet.forEach(vendorId => {
        metricsMap[vendorId] = calculateVendorMetrics(vendorId, allTickets, allBills);
      });
      
      return metricsMap;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
  });
};

